import { useEffect, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { SettingsProvider } from "./context/SettingsContext";
import { WishlistProvider } from "./context/WishlistContext";
import { CartProvider } from "./context/CartContext";
import { ShopModeProvider } from "./context/ShopModeContext";
import { ShopChoiceModal } from "./components/ShopChoiceModal";
import { CartDrawer } from "./components/CartDrawer";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { Navbar } from "./components/Navbar";
import { Footer } from "./components/Footer";
import { Toaster } from "./components/ui/sonner";
import { FestiveEffects } from "./components/FestiveEffects";

// Route-level code-splitting to isolate massive pages into separate chunks
const Home = lazy(() => import("./pages/Home"));
const Products = lazy(() => import("./pages/Products"));
const ProductDetail = lazy(() => import("./pages/ProductDetail"));
const AuthPage = lazy(() => import("./pages/AuthPage"));
const CustomRequest = lazy(() => import("./pages/CustomRequest"));
const MyOrders = lazy(() => import("./pages/MyOrders"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const UserDashboard = lazy(() => import("./pages/UserDashboard"));
const Wishlist = lazy(() => import("./pages/Wishlist"));
const Checkout = lazy(() => import("./pages/Checkout"));
const CopyOtpPage = lazy(() => import("./pages/CopyOtpPage"));
const Faqs = lazy(() => import("./pages/Faqs"));
const PolicyPage = lazy(() => import("./pages/PolicyPage"));
const WholesaleCategoryPage = lazy(() => import("./pages/WholesaleCategoryPage"));

import { trackPageView } from "./lib/metaPixel";

const Dashboard = () => {
  const { user, loading } = useAuth();
  if (loading) return <div className="pt-navbar-clear text-center text-stone-400 py-16">Loading...</div>;
  if (!user) return <Navigate to="/auth" replace />;
  if (user.role === "admin" || user.role === "staff") return <AdminDashboard />;
  return <UserDashboard />;
};

const AppRouter = () => {
  const { user } = useAuth();
  const location = useLocation();
  const isDashboard =
    location.pathname.startsWith("/dashboard") ||
    location.pathname.startsWith("/account") ||
    location.pathname.startsWith("/profile");

  useEffect(() => {
    window.scrollTo(0, 0);
    trackPageView(location.pathname);
  }, [location.pathname]);

  // Staff accounts operate exclusively within the staff operations dashboard
  if (user?.role === "staff" && !isDashboard) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <>
      {!isDashboard && <Navbar />}
      <Suspense
        fallback={
          <div className="min-h-[60vh] flex flex-col items-center justify-center py-20 text-stone-400">
            <div className="w-9 h-9 border-2 border-stone-200 border-t-amber-600 rounded-full animate-spin"></div>
            <span className="text-xs text-stone-400 mt-3 font-medium">Loading page...</span>
          </div>
        }
      >
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/retail" element={<Home />} />
          <Route path="/wholesale" element={<Home />} />
          <Route path="/wholesale/category/:categoryId" element={<WholesaleCategoryPage />} />
          <Route path="/wholesale/category/:categoryId/:subCategoryId" element={<WholesaleCategoryPage />} />
          <Route path="/products" element={<Products />} />
          <Route path="/products/:id" element={<ProductDetail />} />
          <Route path="/auth" element={<AuthPage />} />
          <Route path="/login" element={<AuthPage />} />
          <Route path="/register" element={<AuthPage />} />
          <Route path="/signup" element={<AuthPage />} />
          <Route path="/copy-otp" element={<CopyOtpPage />} />
          <Route path="/custom-request" element={<CustomRequest />} />
          <Route path="/requirements" element={<CustomRequest />} />
          <Route path="/orders" element={<MyOrders />} />
          <Route path="/my-orders" element={<MyOrders />} />
          <Route path="/wishlist" element={<Wishlist />} />
          <Route path="/faqs" element={<Faqs />} />
          <Route path="/faq" element={<Faqs />} />
          <Route path="/shipping-policy" element={<PolicyPage policyType="shipping" />} />
          <Route path="/shipping" element={<PolicyPage policyType="shipping" />} />
          <Route path="/return-policy" element={<PolicyPage policyType="return" />} />
          <Route path="/refund-policy" element={<PolicyPage policyType="return" />} />
          <Route path="/returns" element={<PolicyPage policyType="return" />} />
          <Route path="/about" element={<PolicyPage policyType="about" />} />
          <Route path="/about-us" element={<PolicyPage policyType="about" />} />
          <Route path="/terms" element={<PolicyPage policyType="terms" />} />
          <Route path="/terms-and-conditions" element={<PolicyPage policyType="terms" />} />
          <Route path="/privacy" element={<PolicyPage policyType="privacy" />} />
          <Route path="/privacy-policy" element={<PolicyPage policyType="privacy" />} />
          <Route path="/policy/:type" element={<PolicyPage />} />
          <Route path="/checkout" element={<Checkout />} />
          <Route path="/checkout/:id" element={<Checkout />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/profile" element={<Dashboard />} />
          <Route path="/account" element={<Dashboard />} />
        </Routes>
      </Suspense>
      {!isDashboard && <Footer />}
    </>
  );
};

const AppContent = () => {
  return (
    <AuthProvider>
      <WishlistProvider>
        <CartProvider>
          <ShopModeProvider>
            <BrowserRouter>
              <ErrorBoundary>
                <FestiveEffects />
                <ShopChoiceModal />
                <CartDrawer />
                <AppRouter />
              </ErrorBoundary>
            </BrowserRouter>
          </ShopModeProvider>
        </CartProvider>
      </WishlistProvider>
    </AuthProvider>
  );
};

function App() {
  useEffect(() => {
    const blockCtx = (e) => e.preventDefault();
    const blockKeys = (e) => {
      const k = (e.key || "").toUpperCase();
      if (k === "F12" || (e.ctrlKey && e.shiftKey && ["I", "J", "C"].includes(k)) || (e.ctrlKey && k === "U")) {
        e.preventDefault();
      }
    };
    document.addEventListener("contextmenu", blockCtx);
    document.addEventListener("keydown", blockKeys);
    return () => {
      document.removeEventListener("contextmenu", blockCtx);
      document.removeEventListener("keydown", blockKeys);
    };
  }, []);

  return (
    <div className="App">
      <SettingsProvider>
        <AppContent />
      </SettingsProvider>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;
